import { CircularProgress } from "@mui/material";
import { cn } from "@utils";
import { XIcon } from "lucide-react";
import { ReactNode } from "react";
import { AttachmentSaveMenu, AttachmentStorage } from "./AttachmentSaveMenu";

export type AttachmentListItem = {
  id: string;
  title: string;
  icon: ReactNode;
  storage?: AttachmentStorage;
  status?: "uploading" | "parsing" | "ready" | "failed";
  error?: string;
  onRemove?: () => void;
  onRetry?: () => void;
};

export const AttachmentList = ({
  items,
  actions,
  disabled = false,
  className,
}: {
  items: AttachmentListItem[];
  actions?: ReactNode;
  disabled?: boolean;
  className?: string;
}) => (
  <ul
    aria-label="Attachments"
    className={cn(
      "m-0 flex list-none flex-wrap items-center gap-2 p-0",
      className
    )}
  >
    {actions && (
      <li role="presentation" className="flex items-center gap-2">
        {actions}
      </li>
    )}
    {items.map((item) => (
      <li
        key={item.id}
        className={cn(
          "flex max-w-full items-center gap-1 rounded-md border bg-gray-100 px-2 py-1 text-gray-500",
          item.storage?.destination === "chat"
            ? "border-dashed border-gray-400"
            : "border-solid",
          item.storage?.destination === "eqms"
            ? "border-blue-300"
            : !item.storage && "border-gray-300"
        )}
      >
        {item.status === "uploading" || item.status === "parsing" ? (
          <CircularProgress
            size={14}
            className="shrink-0 text-blue-500"
            aria-label={`${item.status === "uploading" ? "Uploading" : "Parsing"} ${item.title}`}
          />
        ) : (
          item.icon
        )}
        <span className="text-xs whitespace-normal break-words [overflow-wrap:anywhere]">
          {item.title}
        </span>
        {item.status && (
          <span
            role="status"
            className={cn(
              "text-xs",
              item.status === "ready" && "sr-only",
              item.status === "failed" && "text-red-600"
            )}
          >
            {item.status === "ready"
              ? "Ready"
              : item.status === "uploading"
                ? "Uploading…"
                : item.status === "parsing"
                  ? "Parsing…"
                  : item.error}
          </span>
        )}
        {item.storage && (
          <AttachmentSaveMenu
            title={item.title}
            storage={item.storage}
            disabled={disabled}
          />
        )}
        {item.onRetry && (
          <button
            type="button"
            aria-label={`Retry ${item.title}`}
            onClick={item.onRetry}
            disabled={disabled}
            className="cursor-pointer shrink-0 border-0 bg-transparent p-0 text-xs text-primary disabled:cursor-default disabled:opacity-50"
          >
            Retry
          </button>
        )}
        {item.onRemove && (
          <button
            type="button"
            aria-label={`Remove ${item.title}`}
            onClick={item.onRemove}
            disabled={disabled}
            className="cursor-pointer flex items-center justify-center shrink-0 border-0 bg-transparent p-0 disabled:cursor-default disabled:opacity-50"
          >
            <XIcon className="size-[14px] text-gray-500" />
          </button>
        )}
      </li>
    ))}
  </ul>
);
